/**
 * Wood Roughness Variation — per-mesh subtle visual differences.
 *
 * Injects a position-seeded variation into MeshStandardMaterial's
 * fragment shader via onBeforeCompile. Each mesh that shares the
 * varied material gets a unique tint/roughness shift based on its
 * world-space position, creating natural wood appearance without
 * visible repetition.
 *
 * The shader reads the fragment's world position and feeds it through
 * a simple hash to produce three offsets:
 *   - hue shift    ±0.015  (barely visible, prevents uniform color)
 *   - brightness   ±0.04   (subtle lightness variation per board)
 *   - roughness    ±0.06   (some boards slightly smoother/rougher)
 *
 * These values are intentionally small: the goal is naturalness,
 * not patchwork.
 */
import type * as THREE from "three";

/* ─── Variation GLSL ─────────────────────────────────────────── */

/**
 * Simple hash that produces a [-1, 1] value from a world position.
 * The quantization step (floor) ensures each discrete plank segment
 * maps to the same variation value.
 */
const VARIATION_GLSL = /* glsl */ `
  float woodHash(vec2 co) {
    return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453) * 2.0 - 1.0;
  }

  // Quantize position to board-scale cells so each plank gets
  // a stable value. The cell size (0.12 m) roughly matches
  // typical panel/board widths.
  vec2 boardCell(vec3 worldPos) {
    return floor(worldPos.xz * 8.33);  // 1/0.12 ≈ 8.33
  }

  // Smooth value noise in [0, 1] for the fibre relief and sheen patches.
  float woodNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = woodHash(i) * 0.5 + 0.5;
    float b = woodHash(i + vec2(1.0, 0.0)) * 0.5 + 0.5;
    float c = woodHash(i + vec2(0.0, 1.0)) * 0.5 + 0.5;
    float d = woodHash(i + vec2(1.0, 1.0)) * 0.5 + 0.5;
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  // World metres across and along the board: on a wall the boards stand,
  // so "along" is Y and "across" is the horizontal axis in the wall plane.
  vec2 woodBoardAxes(vec3 wp, vec3 worldNormal, float flatAcross, float flatAlong) {
    vec3 absN = abs(normalize(worldNormal));
    float wallSurface = step(absN.y, max(absN.x, absN.z));
    float wallAcross = mix(wp.z, wp.x, step(absN.x, absN.z));
    return vec2(
      mix(flatAcross, wallAcross, wallSurface),
      mix(flatAlong, wp.y, wallSurface)
    );
  }

  // Height of the sawn fibres: long thin ridges along the board (about
  // 3 mm apart, 15 cm long) over a finer fuzz.
  float woodFibreHeight(vec2 axes) {
    float wander = woodNoise(vec2(axes.x * 18.0, axes.y * 2.0)) * 2.4;
    float ridges = woodNoise(vec2(axes.x * 340.0 + wander, axes.y * 6.5));
    float fuzz = woodNoise(vec2(axes.x * 900.0, axes.y * 22.0));
    return ridges * 0.7 + fuzz * 0.3;
  }
`;

/* ─── Shader injection helpers ───────────────────────────────── */

export interface WoodVariationParams {
	/** Max hue rotation in radians. Default: 0.015 */
	hueShift?: number;
	/** Max brightness multiplier offset. Default: 0.04 */
	brightnessRange?: number;
	/** Max roughness offset. Default: 0.06 */
	roughnessRange?: number;
	/** Low-frequency wall-scale tonality breakup. Default: 0.035 */
	macroVariation?: number;
	/** Darkening near perceived board boundaries. Default: 0.045 */
	grooveStrength?: number;
	/** Warm lift on grazing angles. Default: 0.04 */
	grazingLift?: number;
	/**
	 * Strength of the procedural fine-grain stripe pattern that runs
	 * along the board's long axis. Reads as wood grain ticking at
	 * close range. 0 disables. Default: 0.05
	 */
	grainStrength?: number;
	/**
	 * Grain stripe frequency in lines-per-metre. ~120 reads as fir/
	 * spruce grain at close camera distance; 80 reads as wider aspen
	 * pattern. Default: 100
	 */
	grainFrequency?: number;
	/**
	 * Which world axis the boards RUN ALONG on horizontal (floor/ceiling)
	 * surfaces. The grain stripes must be the fast-varying signal ACROSS
	 * the board width, so this picks which of X/Z is the across axis.
	 *
	 * Default `"z"` matches the standard panel/deck mapping, where a
	 * `BoxGeometry` top face puts the texture's plank axis along Z. The
	 * inner ceiling flips to `"x"` (issue 115: the takgles now runs along
	 * Z, so the panel boards must cross it) — without the flip the grain
	 * ticking runs across the boards instead of with them, which is the
	 * same "wrong ledd" artefact the rotated albedo variant fixes.
	 *
	 * Vertical surfaces are unaffected: there the along-axis is world Y.
	 */
	flatBoardAxis?: "x" | "z";
	/** Boards per raw UV unit for metre-mapped facade sheets. Zero uses spatial cells. */
	boardUvScale?: number;
	/**
	 * Relief of the sawn wood fibres, as a bump on the shading normal: fine
	 * ridges that run WITH the board, a few millimetres apart across it.
	 * Reads at grazing light and in the sheen, not in the albedo, which is
	 * what separates a painted rough-sawn board from a printed photo of
	 * one. Fades out where a fibre gets smaller than a pixel. 0 disables.
	 */
	fibreStrength?: number;
	/**
	 * How much glossier (lower roughness) the smoothest patches of a board
	 * get: slamfärg and oil wear unevenly, so some boards and some stretches
	 * along a board catch the sky while the rest stays chalky. 0 disables.
	 */
	sheenVariation?: number;
}

const DEFAULT_PARAMS: Required<WoodVariationParams> = {
	hueShift: 0.015,
	brightnessRange: 0.04,
	roughnessRange: 0.06,
	macroVariation: 0.035,
	grooveStrength: 0.045,
	grazingLift: 0.04,
	grainStrength: 0.05,
	grainFrequency: 100,
	flatBoardAxis: "z",
	boardUvScale: 0,
	fibreStrength: 0,
	sheenVariation: 0,
};

type ShaderStage = "vertex" | "fragment";

function replaceShaderChunkOrThrow(
	shaderSource: string,
	chunk: string,
	replacement: string,
	stage: ShaderStage,
): string {
	if (!shaderSource.includes(chunk)) {
		throw new Error(
			`[woodVariation] Missing ${stage} shader chunk "${chunk}". ` +
				"Three.js shader layout changed; update applyWoodVariation injection points.",
		);
	}

	return shaderSource.replace(chunk, replacement);
}

/**
 * Apply wood variation to an existing MeshStandardMaterial.
 * Mutates the material's `onBeforeCompile` — safe to call once
 * during material creation.
 */
export function applyWoodVariation(
	material: THREE.MeshStandardMaterial,
	params: WoodVariationParams = {},
): void {
	const p = { ...DEFAULT_PARAMS, ...params };

	// See edgeBevel.ts for the rationale: parameter values baked into
	// the shader source are invisible to Three.js's program cache, so
	// without a custom key two materials with different params would
	// silently share the first compiled variant. Chain onto any prior
	// key so this layers with applyEdgeBevel.
	const existingCacheKey = material.customProgramCacheKey?.bind(material);
	const ourKey =
		`wood-variation:${p.hueShift.toFixed(4)}:${p.brightnessRange.toFixed(4)}:` +
		`${p.roughnessRange.toFixed(4)}:${p.macroVariation.toFixed(4)}:` +
		`${p.grooveStrength.toFixed(4)}:${p.grazingLift.toFixed(4)}:` +
		`${p.grainStrength.toFixed(4)}:${p.grainFrequency.toFixed(1)}:` +
		`${p.flatBoardAxis}:${p.boardUvScale.toFixed(2)}:` +
		`${p.fibreStrength.toFixed(4)}:${p.sheenVariation.toFixed(4)}`;
	material.customProgramCacheKey = () =>
		existingCacheKey ? `${existingCacheKey()}|${ourKey}` : ourKey;

	material.onBeforeCompile = (shader) => {
		// ── Vertex shader: declare varying and pass world position ──
		// Inject AFTER #include <common> so we are past precision directives.
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <common>",
			`#include <common>
varying vec3 vWoodWorldPos;
varying vec3 vWoodWorldNormal;
varying vec2 vWoodUv;`,
			"vertex",
		);
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <worldpos_vertex>",
			`#include <worldpos_vertex>
vWoodUv = uv;
vWoodWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
vWoodWorldNormal = normalize(mat3(modelMatrix) * normal);`,
			"vertex",
		);

		// ── Fragment shader: declare varying + hash helpers ──────
		// Inject AFTER #include <common> so we are past precision directives.
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <common>",
			`#include <common>
varying vec3 vWoodWorldPos;
varying vec3 vWoodWorldNormal;
varying vec2 vWoodUv;
${VARIATION_GLSL}`,
			"fragment",
		);

		// After diffuseColor is computed (from map * color), apply variation
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <color_fragment>",
			`#include <color_fragment>
{
  vec3 wp = vWoodWorldPos;
  vec2 cell = ${p.boardUvScale > 0 ? `vec2(floor(vWoodUv.x * ${p.boardUvScale.toFixed(2)}), 0.0)` : "boardCell(wp)"};
  vec2 macroCell = floor(wp.xz * 1.45);

  float h1 = woodHash(cell);
  float h2 = woodHash(cell + vec2(127.1, 311.7));
  float h3 = woodHash(cell + vec2(269.5, 183.3));
  float hMacro = woodHash(macroCell + vec2(19.1, 73.7));
  float hMacroWarm = woodHash(macroCell + vec2(131.2, 17.4));

  // Hue shift via simple RGB rotation approximation
  float hueAngle = h1 * ${p.hueShift.toFixed(4)};
  float cosA = cos(hueAngle);
  float sinA = sin(hueAngle);
  mat3 hueRot = mat3(
    cosA + (1.0 - cosA) / 3.0,
    (1.0 - cosA) / 3.0 - sqrt(1.0/3.0) * sinA,
    (1.0 - cosA) / 3.0 + sqrt(1.0/3.0) * sinA,
    (1.0 - cosA) / 3.0 + sqrt(1.0/3.0) * sinA,
    cosA + (1.0 - cosA) / 3.0,
    (1.0 - cosA) / 3.0 - sqrt(1.0/3.0) * sinA,
    (1.0 - cosA) / 3.0 - sqrt(1.0/3.0) * sinA,
    (1.0 - cosA) / 3.0 + sqrt(1.0/3.0) * sinA,
    cosA + (1.0 - cosA) / 3.0
  );
  diffuseColor.rgb = hueRot * diffuseColor.rgb;

  // Brightness shift
  diffuseColor.rgb *= 1.0 + h2 * ${p.brightnessRange.toFixed(4)};
  diffuseColor.rgb *= 1.0 + hMacro * ${p.macroVariation.toFixed(4)};

  float boardAxis = fract((wp.x + wp.z * 0.35) * 8.33);
  float grooveEdge = min(boardAxis, 1.0 - boardAxis);
  float grooveMask = 1.0 - smoothstep(0.0, 0.085, grooveEdge);
  diffuseColor.rgb *= 1.0 - grooveMask * ${p.grooveStrength.toFixed(4)};

  vec3 viewDir = normalize(cameraPosition - wp);
  float grazing = pow(1.0 - max(dot(normalize(vWoodWorldNormal), viewDir), 0.0), 2.0);
  diffuseColor.rgb += vec3(1.0, 0.78, 0.52) * grazing * ${p.grazingLift.toFixed(4)} * (0.75 + hMacroWarm * 0.25);

  // Procedural grain stripes — fine darker lines that run with the
  // board's long axis. The high-frequency signal must vary across
  // the board width; using world Y as the fast coordinate on wall
  // panels creates visible horizontal bands.
  vec3 absN = abs(normalize(vWoodWorldNormal));
  float wallSurface = step(absN.y, max(absN.x, absN.z));
  float wallAcross = mix(wp.z, wp.x, step(absN.x, absN.z));
  float wallAlong = wp.y;
  float flatAcross = wp.${p.flatBoardAxis === "x" ? "z" : "x"};
  float flatAlong = wp.${p.flatBoardAxis};
  float grainAcross = mix(flatAcross, wallAcross, wallSurface);
  float grainAlong = mix(flatAlong, wallAlong, wallSurface);
  float grainHash = woodHash(floor(vec2(grainAcross * 8.0, grainAlong * 1.6)));
  float grainWander = sin(grainAlong * 3.2 + grainHash * 1.4) * 0.45;
  float grainWave = sin(grainAcross * ${p.grainFrequency.toFixed(1)} + grainWander);
  // Two-band: a tight dark stripe core, a wider soft band around it.
  float grainCore = smoothstep(0.55, 0.95, grainWave);
  float grainSoft = smoothstep(0.0, 0.7, grainWave) * 0.4;
  float grain = (grainCore + grainSoft) * ${p.grainStrength.toFixed(4)};
  diffuseColor.rgb *= 1.0 - grain;

  // Clamp to valid range
  diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
}
`,
			"fragment",
		);

		// Roughness variation: after roughnessFactor is read from the map
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <roughnessmap_fragment>",
			`#include <roughnessmap_fragment>
{
  vec3 wp = vWoodWorldPos;
  vec2 cell = ${p.boardUvScale > 0 ? `vec2(floor(vWoodUv.x * ${p.boardUvScale.toFixed(2)}), 0.0)` : "boardCell(wp)"};
  vec2 macroCell = floor(wp.xz * 1.45);
  float h3 = woodHash(cell + vec2(269.5, 183.3));
  float hMacro = woodHash(macroCell + vec2(7.7, 31.9));
  roughnessFactor = clamp(
    roughnessFactor + h3 * ${p.roughnessRange.toFixed(4)} + hMacro * ${Math.max(p.roughnessRange * 0.42, 0.01).toFixed(4)},
    0.0,
    1.0
  );${
		p.sheenVariation > 0
			? `
  // Uneven wear: whole boards and stretches along a board polish up,
  // the rest stays chalky. The patches follow the board, not the wall.
  vec2 sheenAxes = woodBoardAxes(wp, vWoodWorldNormal, wp.${p.flatBoardAxis === "x" ? "z" : "x"}, wp.${p.flatBoardAxis});
  float sheenBoard = woodHash(cell + vec2(51.3, 7.9)) * 0.5 + 0.5;
  float sheenPatch = woodNoise(vec2(sheenAxes.x * 3.0 + sheenBoard * 17.0, sheenAxes.y * 1.1));
  float sheen = smoothstep(0.3, 0.85, sheenPatch * 0.65 + sheenBoard * 0.35);
  roughnessFactor = clamp(roughnessFactor - sheen * ${p.sheenVariation.toFixed(4)}, 0.04, 1.0);`
			: ""
	}${
		p.fibreStrength > 0
			? `
  // Fibre tips scatter more than the valleys between them.
  roughnessFactor = clamp(roughnessFactor + (woodFibreHeight(woodBoardAxes(wp, vWoodWorldNormal, wp.${p.flatBoardAxis === "x" ? "z" : "x"}, wp.${p.flatBoardAxis})) - 0.5) * 0.08, 0.04, 1.0);`
			: ""
	}
}
`,
			"fragment",
		);

		if (p.fibreStrength > 0) {
			// Bump from a procedural height, the same screen-derivative method
			// as three's own bumpmap (perturbNormalArb), so it needs no
			// tangents and works on every box face.
			shader.fragmentShader = replaceShaderChunkOrThrow(
				shader.fragmentShader,
				"#include <normal_fragment_maps>",
				`#include <normal_fragment_maps>
{
  vec2 fibreAxes = woodBoardAxes(vWoodWorldPos, vWoodWorldNormal, vWoodWorldPos.${p.flatBoardAxis === "x" ? "z" : "x"}, vWoodWorldPos.${p.flatBoardAxis});
  // A ridge smaller than about a pixel only shimmers: fade the relief out
  // with distance instead of letting it alias.
  float fibreFootprint = fwidth(fibreAxes.x * 340.0);
  float fibreFade = 1.0 - smoothstep(0.35, 0.9, fibreFootprint);
  float fibreH = woodFibreHeight(fibreAxes) * ${p.fibreStrength.toFixed(4)} * fibreFade;
  vec3 fibrePosDx = normalize(dFdx(-vViewPosition));
  vec3 fibrePosDy = normalize(dFdy(-vViewPosition));
  float fibreHx = dFdx(fibreH);
  float fibreHy = dFdy(fibreH);
  vec3 fibreR1 = cross(fibrePosDy, normal);
  vec3 fibreR2 = cross(normal, fibrePosDx);
  float fibreDet = dot(fibrePosDx, fibreR1) * (faceDirection);
  vec3 fibreGrad = sign(fibreDet) * (fibreHx * fibreR1 + fibreHy * fibreR2);
  normal = normalize(abs(fibreDet) * normal - fibreGrad);
}`,
				"fragment",
			);
		}
	};

	// Force shader recompilation
	material.needsUpdate = true;
}
